import { memo, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { isEqual } from "lodash-es";
import { useShallow } from "zustand/react/shallow";

import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { SearchQueryRequestedPage } from "types/generated";
import useFiltersVariables from "components/Filters/useFiltersVariables";

import {
  FILTERS_TYPE,
  GROUPS_LIMIT,
  GROUPS_LIMIT_STORAGE_KEY,
  initialSortDirection,
  initialSortOption,
  PAGINATION_OPTIONS,
  RESOURCES_ROWS_PER_PAGE,
} from "./constants";
import useIacTableStore from "../components/Table/useIacTableStore";
import IacManagementTable from "../components/Table";
import useIacManagementStore from "../useIacManagementStore";
import IacManagementEmpty from "../components/Empty";
import useIacManagementGroups from "../useIacManagementGroups";
import IacManagementTableGroupRow from "./TableGroupRow";
import IacManagementTableResourceRow from "./TableResourceRow";
import useIacManagementAnalytics from "../useIacManagementAnalytics";
import { IacManagementGroupBy } from "../types";
import useIacManagementResources from "../useIacManagementResources";

const IacManagementListLayout = () => {
  const { analyticPage } = useIacManagementAnalytics();

  const { setGroups, flushTable, loadResources, openGroup } = useIacTableStore(
    useShallow((state) => ({
      setGroups: state.setGroups,
      flushTable: state.flushTable,
      loadResources: state.loadResources,
      openGroup: state.openGroup,
    }))
  );

  const { allGroupsQty, setPageIsEmpty, groupBy, parentStackId } = useIacManagementStore(
    useShallow((state) => ({
      allGroupsQty: state.allItemsQty,
      setPageIsEmpty: state.setPageIsEmpty,
      groupBy: state.groupBy,
      parentStackId: state.parentStackId,
    }))
  );

  const { groups, loading, fetchData, endCursor, startCursor, isEmpty, searchIsEmpty } =
    useIacManagementGroups({ groupBy, parentStackId });
  const { fetchData: fetchResources } = useIacManagementResources();
  const stackResourcesIds = useIacTableStore((state) =>
    parentStackId ? state.resourcesIdsPerGroup[parentStackId] : undefined
  );

  const { filterVariables, filterVariablesLoaded, sortingMounted } = useFiltersVariables({
    filterType: FILTERS_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const isStackUngrouped = !!parentStackId && groupBy === IacManagementGroupBy.None;
  const waitingForStackResources = isStackUngrouped && stackResourcesIds === undefined;
  const listLoading = !filterVariablesLoaded || loading || waitingForStackResources;

  const paginationResetKey = useMemo(
    () => ({ filterVariables, groupBy, parentStackId }),
    [filterVariables, groupBy, parentStackId]
  );

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: fetchData,
    endCursor,
    startCursor,
    allItemsCount: allGroupsQty,
    visibleItemsCount: groups.length,
    initialLimit: GROUPS_LIMIT,
    storageKey: GROUPS_LIMIT_STORAGE_KEY,
    analyticsPage: analyticPage,
    resetKey: paginationResetKey,
    loading: listLoading,
  });

  const showPagination = groupBy !== IacManagementGroupBy.None && !isEmpty;

  useEffect(() => {
    setPageIsEmpty(isEmpty);
  }, [isEmpty, setPageIsEmpty]);

  const flushKey = useMemo(() => ({ filterVariables, groupBy }), [filterVariables, groupBy]);
  const previousFlushKey = useRef<typeof flushKey | undefined>(undefined);

  useEffect(() => {
    if (!filterVariablesLoaded || !sortingMounted) {
      return;
    }

    const previous = previousFlushKey.current;
    previousFlushKey.current = flushKey;

    if (previous && !isEqual(previous, flushKey)) {
      flushTable();
    }
  }, [flushKey, flushTable, filterVariablesLoaded, sortingMounted]);

  useEffect(() => {
    if (!filterVariablesLoaded || !sortingMounted) {
      return;
    }

    fetchData({
      cursor: null,
      requestedPage: SearchQueryRequestedPage.First,
      rowsPerPage,
    });
  }, [fetchData, filterVariables, rowsPerPage, filterVariablesLoaded, sortingMounted]);

  // the table renders rows from the store, so the mirror has to land before paint or the row area
  // flashes empty; filterVariables re-fills it after flushTable() emptied it
  useLayoutEffect(() => {
    setGroups(groups);
  }, [setGroups, groups, filterVariables]);

  useEffect(() => {
    if (
      !parentStackId ||
      groupBy !== IacManagementGroupBy.None ||
      !sortingMounted ||
      stackResourcesIds !== undefined
    ) {
      return;
    }

    openGroup(parentStackId);
    loadResources(
      {
        groupId: parentStackId,
        parentStackId,
        groupBy,
        rowsPerPage: RESOURCES_ROWS_PER_PAGE,
      },
      fetchResources
    );
  }, [
    fetchResources,
    groupBy,
    loadResources,
    parentStackId,
    openGroup,
    sortingMounted,
    stackResourcesIds,
  ]);

  return (
    <>
      {isEmpty && <IacManagementEmpty />}
      {searchIsEmpty && <IacManagementEmpty hasNoSearchResult />}
      {!searchIsEmpty && !isEmpty && (
        <IacManagementTable
          groupsLoading={listLoading}
          rowsPerPage={rowsPerPage}
          renderGroupRow={IacManagementTableGroupRow}
          renderResourceRow={IacManagementTableResourceRow}
          hasPagination={showPagination}
        />
      )}

      {showPagination && (
        <TablePagination
          from={from}
          to={to}
          loading={listLoading}
          goToNextPage={goToNextPage}
          setRowsPerPage={setRowsPerPage}
          goToPrevPage={goToPrevPage}
          goToFirstPage={goToFirstPage}
          goToLastPage={goToLastPage}
          rowsPerPage={rowsPerPage}
          itemsCount={allGroupsQty}
          lastPage={lastPage}
          page={page}
          paginationOptions={PAGINATION_OPTIONS}
        />
      )}
    </>
  );
};

export default memo(IacManagementListLayout);
